import { useCallback, useEffect, useRef, useState } from 'react';
import { trackError, ErrorSeverity } from '../utils/error-tracking';
import {
  pageSuspendedSince,
  probeOwnOrigin,
  settleBeforeRetry,
  severityForOwnFetchFailure,
  shouldRetryTransportFailure,
  transportFailureContext,
} from '../utils/fetch-transport-failure';
import { isVehicle } from './utils';
import { DEFAULT_SHIPPING_ESTIMATE } from '../utils/shipping-constants';
import {
  readCachedShippingEstimate,
  shippingEstimateCacheKey,
  writeCachedShippingEstimate,
  type CachedShippingEstimate,
} from '../utils/shipping-estimate-cache';
import { canAcceptPayments } from '../data/js/trust-levels';
import { classifyTraffic, isMachine } from '../utils/traffic-class';
import { appendPageLoadAt, appendShuffleSeed } from '../utils/seeded-shuffle';

/**
 * Name a fetch rejection that is not an Error. Native fetch only rejects with a
 * TypeError, so anything else came from a wrapper around it. The message keeps
 * the kind (one kind, one cluster); the detail names what fired it.
 * Null for an Error, which already says what it is.
 */
export function describeFetchRejection(reason: unknown): { message: string; detail: string } | null {
  if (reason instanceof Error) return null;
  if (typeof Event !== 'undefined' && reason instanceof Event) {
    const target = reason.target as { tagName?: unknown; src?: unknown; href?: unknown } | null;
    const tag = typeof target?.tagName === 'string' ? target.tagName : 'no target';
    const url = typeof target?.src === 'string' ? target.src : typeof target?.href === 'string' ? target.href : '';
    return {
      message: `Shipping estimate fetch rejected with a "${reason.type}" event (${tag})`,
      detail: `${reason.type} on ${tag}${url ? ` ${url.slice(0, 200)}` : ''}`,
    };
  }
  const kind = reason === null ? 'null' : typeof reason;
  // String() can throw on an object with no prototype; this runs inside a catch.
  const detail = typeof reason === 'object' && reason !== null
    ? String((reason as { constructor?: { name?: unknown } }).constructor?.name ?? 'Object')
    : String(reason).slice(0, 200);
  return { message: `Shipping estimate fetch rejected with a non-Error (${kind})`, detail };
}

/**
 * Hook to preload similar listings for flip buttons.
 */
export function usePreloadSimilarListings({
  id,
  setSimilarListings,
  currentListGroup,
  similarList,
}: {
  id: string;
  setSimilarListings: (listings: unknown[]) => void;
  currentListGroup: string;
  similarList: unknown[];
}) {
  const hasPreloadedSimilar = useRef<string | null>(null);

  // Reset preload ref when id changes and not navigating via similarList
  useEffect(() => {
    if (currentListGroup !== 'similarList') {
      hasPreloadedSimilar.current = null;
      setSimilarListings([]);
    }
  }, [id, currentListGroup, setSimilarListings]);

  const preloadSimilarListings = useCallback(async () => {
    // Skip if already navigating through similarList - keep using existing list
    if (currentListGroup === 'similarList' && similarList.length > 0) {
      return;
    }
    // Skip if already preloaded for this product
    if (hasPreloadedSimilar.current === id || !id) return;
    hasPreloadedSimilar.current = id;

    // Which step threw. The 2026-10-20 row dropped the error entirely and
    // carried no context, so it could not say whether the request was cut,
    // refused, or answered with a body that did not parse.
    let stage: 'fetch' | 'parse' = 'fetch';
    let status: number | null = null;
    try {
      const response = await fetch(`/api/search/similar?${appendPageLoadAt(appendShuffleSeed(new URLSearchParams({ listingId: String(id), limit: '12' })))}`);
      stage = 'parse';
      status = response.status;
      const data = await response.json();
      if (data.success && data.listings?.length > 0) {
        setSimilarListings(data.listings);
      }
    } catch (err) {
      const transport = transportFailureContext(err);
      trackError(
        '[ProductDetail] Similar listings preload error',
        severityForOwnFetchFailure(err, ErrorSeverity.WARNING, transport),
        {
          component: 'ProductDetail',
          action: 'preloadSimilarListings',
          extra: {
            stage,
            status,
            listingId: String(id),
            errorName: err instanceof Error ? err.name : typeof err,
            errorMessage: err instanceof Error ? err.message : String(err),
            ...transport,
          },
        }
      );
    }
  }, [id, setSimilarListings, currentListGroup, similarList]);

  return { preloadSimilarListings, hasPreloadedSimilar };
}

/** What a non-OK shipping-estimate body turned out to be. */
interface RefusalBody {
  /** The `errCode`, when the body carried one. Null means "not a labelled refusal". */
  code: string | null;
  /** `json` when the body parsed, `unparseable` when it did not. */
  kind: 'json' | 'unparseable';
  /** Top-level keys of the parsed body, comma-joined. Null when it did not parse. */
  keys: string | null;
  /** The body's own `error` sentence, truncated. Null when there wasn't one. */
  message: string | null;
}

/** Longer than any sentence our own routes emit; a guard, not a budget. */
const REFUSAL_MESSAGE_LIMIT = 120;

/**
 * Read a non-OK shipping-estimate body for its `errCode`, and — when there
 * isn't one — for enough shape to say who answered.
 *
 * An unlabelled 4xx is the one case the caller still reports, and the row it
 * files could not previously say whether the refusal came from our own handler
 * at all. That mattered: every 400 `/api/shipping/estimate` writes itself
 * carries an `errCode` except two, and both of those need a `listingId` that
 * fails `parseInt` — which the product page cannot send, because it reads the
 * id out of a numeric route. So an unlabelled 400 on a numeric listing is
 * something between the browser and the route, and the row needs to prove that
 * rather than leave the next reader to infer it.
 *
 * `kind` settles it on its own: our handler always answers JSON, an edge or
 * proxy error page does not. `keys` and `message` name which branch when it was
 * ours. The message is one of the route's own literal sentences, never user
 * input, and is truncated regardless.
 *
 * String and number are both accepted as codes because both are in use: the
 * shipping routes emit names (`SHIPPING_INVALID_FROM_ADDRESS`), the account
 * routes emit the legacy numeric codes.
 */
async function readRefusalBody(response: Response): Promise<RefusalBody> {
  let body: unknown;
  try {
    body = await response.json();
  // eslint-disable-next-line sellwild-code/catch-must-log -- the unparseable body IS the finding; it is returned as `kind` and the caller logs it with the refusal it belongs to
  } catch {
    return { code: null, kind: 'unparseable', keys: null, message: null };
  }

  const record = typeof body === 'object' && body !== null
    ? (body as Record<string, unknown>)
    : null;
  const keys = record ? Object.keys(record).sort().join(',') : '';
  const message = typeof record?.error === 'string'
    ? record.error.slice(0, REFUSAL_MESSAGE_LIMIT)
    : null;

  const rawCode = record?.errCode;
  let code: string | null = null;
  if (typeof rawCode === 'string' && rawCode.length > 0) code = rawCode;
  else if (typeof rawCode === 'number') code = String(rawCode);

  return { code, kind: 'json', keys, message };
}

/** What the page shows before a listing has been quoted, or when quoting fails. */
const DEFAULT_ESTIMATE: CachedShippingEstimate = {
  shippingEstimate: DEFAULT_SHIPPING_ESTIMATE,
  shippingCarrier: 'USPS',
  shipsFrom: null,
  isOutOfStock: false,
};

/**
 * Hook to fetch shipping estimate for a product.
 *
 * Quotes are cached per listing, variant and buyer for the life of the tab, so
 * the flip buttons can walk back and forth over a list without re-asking
 * `/api/shipping/estimate` for an answer it has already given.
 */
export function useShippingEstimate({
  product,
  isGCCorMyEV,
  isPartnerListing,
  user,
  loggedIn,
  dataSourceId,
  selectedVariantVid,
}: {
  product: Record<string, unknown> | null;
  isGCCorMyEV: boolean;
  isPartnerListing: boolean;
  user: Record<string, unknown>;
  loggedIn: boolean;
  dataSourceId: unknown;
  selectedVariantVid?: string | null;
}) {
  const [estimate, setEstimate] = useState<CachedShippingEstimate>(DEFAULT_ESTIMATE);
  const [loadingShipping, setLoadingShipping] = useState(false);

  // What this listing's quote is filed under, for this buyer and this variant.
  const cacheKey = shippingEstimateCacheKey({
    listingId: product?.id as string | number | null | undefined,
    variantVid: selectedVariantVid,
    userId: user?.id as string | number | null | undefined,
  });

  // A flip to a listing already quoted this session resolves during render, not
  // after one. The effect below reaches the same cached value, but it runs after
  // the browser has painted, so the buyer would see the previous listing's price
  // flash before this one's — which is exactly what flipping back was meant to
  // stop. This is React's own "adjusting state when a prop changes" pattern:
  // remembering which key the shown estimate belongs to keeps it to a single
  // update per flip, and re-rendering from here costs no paint.
  const [shownFor, setShownFor] = useState<string | null>(cacheKey);
  if (shownFor !== cacheKey) {
    setShownFor(cacheKey);
    const alreadyQuoted = readCachedShippingEstimate(cacheKey);
    if (alreadyQuoted) {
      setEstimate(alreadyQuoted);
      setLoadingShipping(false);
    }
  }

  useEffect(() => {
    // Recalculate isBuyable inside effect to get fresh values.
    // Mirrors useProductDetailState: owners see the buyer's order breakdown, so
    // their own listing still needs a real shipping estimate to fill it in.
    const currentIsBuyable = !isGCCorMyEV &&
      !isPartnerListing &&
      canAcceptPayments((user as Record<string, unknown>)?.trustLevel as number);

    const fetchShippingEstimate = async () => {
      // Don't fetch shipping for vehicles - they have separate shipping
      if (isVehicle(product)) {
        setEstimate({ ...DEFAULT_ESTIMATE, shippingEstimate: 0 });
        setLoadingShipping(false);
        return;
      }

      // Only fetch for buyable products (has Buy Now button)
      if (!currentIsBuyable) {
        setEstimate(DEFAULT_ESTIMATE);
        setLoadingShipping(false);
        return;
      }

      // Skip the fetch only when the product itself isn't shippable. Anonymous
      // visitors get a geo-based estimate server-side (Vercel edge headers).
      if (!product?.id || !product?.shippable) {
        setEstimate(DEFAULT_ESTIMATE);
        setLoadingShipping(false);
        return;
      }

      // The same listing, variant and buyer produce the same quote every time,
      // and the flip buttons walk over the same few listings repeatedly. Serve
      // what was already quoted rather than paying for the round trip again.
      //
      // This also absorbs the re-runs this effect gets for free: `product` and
      // `user` are object props, so a parent re-render with fresh identities
      // used to refetch a listing the buyer had not moved off.
      const alreadyQuoted = readCachedShippingEstimate(cacheKey);
      if (alreadyQuoted) {
        setEstimate(alreadyQuoted);
        setLoadingShipping(false);
        return;
      }

      // Note: Dimensions are optional - API will use default package size if missing

      setLoadingShipping(true);
      // Remember how far the request got. A network failure, a non-OK response
      // whose body is not JSON, and a malformed success body all land in the
      // same catch, and the logged row cannot tell them apart without this.
      let responseStatus: number | null = null;
      let stage: 'fetch' | 'response' | 'parse' = 'fetch';
      // What a 4xx body turned out to be, when one was read. Null at every
      // other stage, which is itself the answer: the failure was not a refusal.
      let refusalBody: RefusalBody | null = null;
      // How long the request ran before it failed. A cut that lands in a few
      // milliseconds is the browser or an extension refusing to send it; one
      // that lands after seconds is the request dying on the way to our route.
      const startedAt = Date.now();
      // Whether a first transport cut was retried. The rows from 2026-09-24
      // were visible, online, not unloading pages cut in a quarter second --
      // a request one more attempt can still answer.
      let retried = false;
      // Whether the request lived through the device sleeping or the tab being
      // hidden or frozen. That cut is the device's, and its retry waits for
      // the network to come back.
      let suspended = false;
      // How long each attempt ran before it ended, in order. elapsedMs alone
      // cannot split them: 2026-10-30 23:17Z, a visible, online Windows
      // desktop was cut twice in 1556ms and the origin probe failed, while
      // that very row reached us by beacon. A cut in a few milliseconds is the
      // browser refusing to send (an extension, a network policy); one that
      // runs for seconds is the network giving up on the way.
      const attemptMs: number[] = [];
      const timedFetch = async (url: string): Promise<Response> => {
        const attemptStartedAt = Date.now();
        try {
          return await fetch(url);
        } finally {
          attemptMs.push(Date.now() - attemptStartedAt);
        }
      };
      try {
        let estimateUrl = `/api/shipping/estimate?listingId=${product.id}`;
        if (selectedVariantVid) {
          estimateUrl += `&variantVid=${encodeURIComponent(selectedVariantVid)}`;
        }
        let response: Response;
        try {
          response = await timedFetch(estimateUrl);
        } catch (firstCut) {
          if (!shouldRetryTransportFailure(firstCut)) throw firstCut;
          retried = true;
          suspended = await settleBeforeRetry(startedAt);
          response = await timedFetch(estimateUrl);
        }
        responseStatus = response.status;
        stage = 'parse';

        // A gateway timeout or a 5xx answers with an HTML error page, not JSON.
        // Feeding that to response.json() only ever produced
        // "Unexpected token 'A', \"An error o\"... is not valid JSON" — a
        // SyntaxError that describes the error page instead of the failure, so
        // the logged row could not say which upstream status caused it.
        if (!response.ok) {
          stage = 'response';

          // A labelled 4xx from our own route is a decision, not a failure. The
          // handler answers `{ success: false, errCode }` for the cases it has
          // already recognised — no seller address, an unresolvable buyer zip, a
          // listing that isn't quotable — and it writes its own row for them,
          // carrying the seller id and the shape of the gap. A second row from
          // here adds no diagnosis and counts page views rather than causes:
          // SHIPPING_INVALID_FROM_ADDRESS alone filed 30 client rows in a day
          // for a handful of sellers the server had logged once each, most of
          // them one partner's traffic to the same three listings.
          //
          // Only 4xx, and only with an errCode. A 5xx answers with an HTML error
          // page, and an unlabelled 4xx is one nobody has explained yet — both
          // still deserve a row.
          refusalBody =
            response.status >= 400 && response.status < 500
              ? await readRefusalBody(response)
              : null;

          if (refusalBody && refusalBody.code !== null) {
            // A labelled refusal is a judgement about the listing, not a hiccup:
            // the seller has no origin, the listing doesn't ship. Cache it like
            // any other answer so flipping back doesn't re-ask a question the
            // route has already settled.
            setEstimate(DEFAULT_ESTIMATE);
            writeCachedShippingEstimate(cacheKey, DEFAULT_ESTIMATE);
            return;
          }

          // A 403 that is not our JSON was written by the Vercel firewall,
          // which refuses crawlers on /api/*. A machine's own browser filing it
          // is the firewall working: 17 rows on 2026-10-27, every one
          // HeadlessChrome from Microsoft addresses, once the pagehide flush
          // began delivering them. A person refused this way still files.
          if (
            response.status === 403 &&
            refusalBody?.kind === 'unparseable' &&
            typeof navigator !== 'undefined' &&
            isMachine(classifyTraffic(navigator.userAgent).trafficClass)
          ) {
            setEstimate(DEFAULT_ESTIMATE);
            return;
          }

          throw new Error(`Shipping estimate request failed: HTTP ${response.status}`);
        }

        // The headers can arrive and the body still be cut: all three rows of
        // 2026-10-28/29 were a 200 whose body read died 2-3s in, on a page a
        // partner app had sent to the background. The first-cut retry above never
        // saw it, so the one request that had already been answered was dropped.
        // Ask once more, with the same rules as a cut request.
        let data;
        try {
          data = await response.json();
        } catch (bodyCut) {
          if (retried || !shouldRetryTransportFailure(bodyCut)) throw bodyCut;
          retried = true;
          stage = 'fetch';
          suspended = await settleBeforeRetry(startedAt);
          response = await timedFetch(estimateUrl);
          responseStatus = response.status;
          stage = 'parse';
          if (!response.ok) {
            stage = 'response';
            throw new Error(`Shipping estimate request failed: HTTP ${response.status}`);
          }
          data = await response.json();
        }

        if (data.success && data.estimate && typeof data.estimate.estimateAmount === 'number') {
          const quoted: CachedShippingEstimate = {
            // estimateAmount === 0 is legitimate "Free shipping" (CJ supplier-shipped
            // products) — preserve it instead of falling back to the default.
            shippingEstimate: data.estimate.estimateAmount,
            // Get carrier name from first/cheapest carrier
            shippingCarrier: data.estimate?.carriers?.length > 0
              ? data.estimate.carriers[0].name
              : 'USPS',
            // Use fromZip as the display label — it contains the warehouse city
            // (e.g. "Dallas, TX") or a descriptive label (e.g. "CJ US Warehouse")
            shipsFrom: data.estimate?.fromZip ?? data.estimate?.shipsFrom ?? null,
            isOutOfStock: Boolean(data.estimate.isOutOfStock),
            arrangedSeparately: Boolean(data.estimate.arrangedSeparately),
            // The tax line rides the same answer; nothing extra is fetched.
            taxEstimate: data.estimate.tax?.taxCents ? data.estimate.tax.taxCents / 100 : 0,
            taxLabel: data.estimate.tax?.label,
            taxRatePercent: data.estimate.tax?.ratePercent,
            taxApproximate: data.estimate.tax?.approximate,
          };
          setEstimate(quoted);
          writeCachedShippingEstimate(cacheKey, quoted);
        } else {
          // A 200 carrying no usable estimate is not an answer worth keeping —
          // the next visit to this listing should ask again.
          setEstimate(DEFAULT_ESTIMATE);
        }
      } catch (error) {
        // Pass the real error through so the row keeps its type and stack; the
        // bare string this used to log produced a StringError with an empty
        // stack and no context, which is undiagnosable a day later.
        //
        // A bare "Failed to fetch" at the fetch stage is the browser cutting
        // the request, and the row cannot say why on its own: the visitor
        // navigating away before the estimate lands (partner-referral traffic
        // bounces exactly like this), going offline, or an extension blocking
        // the call all look identical to our route being down. The transport
        // snapshot separates them, and a request the page itself abandoned
        // files at info instead of queueing as an application warning.
        const transport = transportFailureContext(error);
        // Read before the probe below, which would otherwise add its own time.
        const elapsedMs = Date.now() - startedAt;
        // Cut twice: can this browser reach our origin at all? Yes means only
        // the estimate route was cut (a blocker on that URL, or the route
        // dying); no means nothing of ours gets through from here.
        const probeStartedAt = Date.now();
        const originReachable = retried && transport.transportFailure ? await probeOwnOrigin() : null;
        // A probe refused at once is this browser; one that ran long is the path.
        const probeMs = originReachable === null ? null : Date.now() - probeStartedAt;
        // A rejection that is not an Error used to file as "[object Event]",
        // which named neither the event nor what fired it.
        const rejection = describeFetchRejection(error);
        trackError(
          // describeFetchRejection names every non-Error, so it is set here.
          error instanceof Error ? error : new Error(rejection.message),
          severityForOwnFetchFailure(error, ErrorSeverity.WARNING, transport),
          {
            component: 'ProductDetail',
            action: 'fetchShippingEstimate',
            extra: {
              stage,
              status: responseStatus,
              listingId: String(product.id),
              variantVid: selectedVariantVid ?? null,
              // Only ever set for an unlabelled 4xx — the one failure whose row
              // could not say whether our own route answered it.
              refusalKind: refusalBody?.kind ?? null,
              refusalKeys: refusalBody?.keys ?? null,
              refusalMessage: refusalBody?.message ?? null,
              elapsedMs,
              attemptMs,
              retried,
              suspended: suspended || pageSuspendedSince(startedAt),
              originReachable,
              probeMs,
              rejectedWith: rejection?.detail ?? null,
              ...transport,
            },
          }
        );
        // Not cached: a transport failure says nothing about the listing, and
        // the next flip back to it deserves a real attempt.
        setEstimate(DEFAULT_ESTIMATE);
      } finally {
        setLoadingShipping(false);
      }
    };

    if (product?.id) {
      void fetchShippingEstimate();
    }
  }, [product?.id, isGCCorMyEV, isPartnerListing, dataSourceId, user, loggedIn, product, selectedVariantVid, cacheKey]);

  return {
    shippingEstimate: estimate.shippingEstimate,
    shippingCarrier: estimate.shippingCarrier,
    shipsFrom: estimate.shipsFrom,
    loadingShipping,
    isOutOfStock: estimate.isOutOfStock,
    taxEstimate: estimate.taxEstimate ?? 0,
    taxLabel: estimate.taxLabel,
    taxRatePercent: estimate.taxRatePercent,
    taxApproximate: estimate.taxApproximate,
    // Cross-border listing: the page shows "Arranged Separately", as it does
    // for vehicles, rather than a shipping number nobody will be charged.
    shippingArrangedSeparately: Boolean(estimate.arrangedSeparately),
  };
}
